import { TableCell } from '@mui/material';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { OptionallyVisible } from 'components';
import { useNotifications } from 'providers/notifications/useNotifications';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PayoutDestinationType } from 'services/merchants/types';

import QuestionImage from 'assets/images/question.svg';

import { Button } from 'components/buttons';
import { ButtonWithLoading } from 'components/buttons/buttonWithLoading';
import { Panel } from 'components/containers';
import { DataTableCellProps } from 'components/dataTable';
import { ActionMenu, MenuItem } from 'components/dataTable/cells';
import IconCross from 'components/icons/cross';
import { Buttons, Image, ModalControls } from 'components/modal/styles';

import { handleGenericApiError } from 'utils/error';

import { LABELS } from '../../keys';
import { ErrorText, ModalWithImageContent } from '../payoutModal/fiat/styles';
import { Delimiter, ModalContent, StyledDialog } from '../payoutModal/styles';

export const ActionCell = ({ row, passedProps }: DataTableCellProps) => {
  const { t } = useTranslation();
  const { pushToast } = useNotifications();
  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false);

  const { deletePaymentDestinationProps, type } = row;

  const isCryptoDestinationUpdateForbidden = passedProps?.isDirectFlow && type === PayoutDestinationType.Crypto;

  const onChangeAccountClick = () => {
    passedProps?.editPayout();
  };

  const onDeleteAccountClick = () => {
    setConfirmationModalOpen(true);
  };

  const onClose = () => {
    deletePaymentDestinationProps.resetDelete();
    setConfirmationModalOpen(false);
  };

  const onConfirmDeletionClick = async () => {
    const response = await deletePaymentDestinationProps.deleteEntity();

    const { error } = response as { error: SerializedError | FetchBaseQueryError };
    if (handleGenericApiError(error, pushToast)) {
      return;
    }

    onClose();
  };

  if (isCryptoDestinationUpdateForbidden) {
    return null;
  }

  return (
    <>
      <StyledDialog open={isConfirmationModalOpen} scroll="body" disableEscapeKeyDown fullWidth>
        <Panel
          label={t(LABELS.DELETE_MODAL.TITLE)}
          controls={
            <ModalControls>
              <Button flat onClick={onClose}>
                <IconCross />
              </Button>
            </ModalControls>
          }
        >
          <ModalContent>
            <ModalWithImageContent>
              <Image src={QuestionImage} />
              <h1>{t(LABELS.DELETE_MODAL.HEADER)}</h1>
              <p>{t(LABELS.DELETE_MODAL.CONTENT)}</p>
              <OptionallyVisible visible={deletePaymentDestinationProps.deleteError}>
                <ErrorText>{t(LABELS.DELETE_MODAL.ERROR)}</ErrorText>
              </OptionallyVisible>
              <Delimiter />
              <Buttons>
                <Button secondary onClick={onClose}>
                  {t(LABELS.DELETE_MODAL.CANCEL)}
                </Button>
                <ButtonWithLoading isLoading={deletePaymentDestinationProps.isDeleting} onClick={onConfirmDeletionClick}>
                  {t(LABELS.DELETE_MODAL.CONFIRM)}
                </ButtonWithLoading>
              </Buttons>
            </ModalWithImageContent>
          </ModalContent>
        </Panel>
      </StyledDialog>
      <TableCell>
        <ActionMenu title={t(LABELS.ACTIONS.TITLE)}>
          <MenuItem onClick={onChangeAccountClick}>{t(LABELS.ACTIONS.UPDATE_ACCOUNT)} </MenuItem>
          <OptionallyVisible visible={!passedProps?.isDirectFlow}>
            <MenuItem onClick={onDeleteAccountClick}>{t(LABELS.ACTIONS.DELETE_ACCOUNT)} </MenuItem>
          </OptionallyVisible>
        </ActionMenu>
      </TableCell>
    </>
  );
};
